"use client"

import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { X, Send, Edit, CheckCircle, Clock, ChevronDown, Search, Loader2 } from 'lucide-react'
import { useGetAuthUser, useGetContacts, useGetOutreach, useGetSignature, usePostEmailsSendEmail } from "openapi/api/endpoints"
import { toast } from "sonner"
import { CandidateLinkedInOutreach } from "./candidate-linkedin-outreach"
import { useParams } from "@tanstack/react-router"
import { asArray } from "@/lib/as-array"

interface CandidateOutreachProps {
    candidate: any
    onClose: () => void
    /**
     * The job the outreach is written against. The endpoint requires it —
     * `GET /outreach` generates from candidate *and* job context and 400s
     * without a job_id — and the route params only carry one when the record
     * was opened from inside a job. Opened from the candidates list there is no
     * job in the URL, so the caller resolves one and passes it here.
     */
    jobId?: string | null
}

interface OutreachFormValues {
    email: string
    subject: string
    content: string
}

const hasSubject = (value: unknown): value is { subject?: string } =>
    typeof value === "object" && value !== null && "subject" in value

export function CandidateOutreach({ candidate, onClose, jobId: jobIdProp }: CandidateOutreachProps) {
    const [outreachType, setOutreachType] = useState<"ai-email" | "linkedin">("ai-email")
    const [emailSent, setEmailSent] = useState(false)
    const routeParams = useParams({ strict: false }) as Record<string, string | undefined>
    const candidateId = routeParams.candidateId ?? routeParams.candidate_id ?? candidate?.id ?? ""
    const jobId = jobIdProp ?? routeParams.jobId ?? routeParams.job_id ?? ""
    const { data: authUser } = useGetAuthUser()
    const { data:contactInfo } = useGetContacts({candidateId}, {query:{enabled: !!candidateId}})
    const { data: signatures, isLoading: isSignatureLoading } = useGetSignature()
    const { mutateAsync: sendEmail, isPending: isSendingEmail } = usePostEmailsSendEmail()
    const {data,refetch,isRefetching,isFetching,error:outreachError}=useGetOutreach({
        candidate_id: candidateId,
        job_id: jobId,
        type:'email',
    },{query:{
        enabled: Boolean(candidateId && jobId),
        refetchOnWindowFocus:false,
        staleTime:24 * 60 * 60 * 1000,
    }})
    const emailSubject = hasSubject(data) ? (data.subject ?? "") : ""
    const emailBody = data?.body ?? ""

    const defaultSignature = useMemo(() => {
        const list = asArray<NonNullable<typeof signatures>[number]>(signatures)
        const selected =
            list.find((item) => item.is_default && item.is_active) ??
            list.find((item) => item.is_default) ??
            list.find((item) => item.is_active) ??
            list[0]

        if (!selected) return ""
        return (((selected as any).text ?? selected.text_signature ?? "") as string).trim()
    }, [signatures])

    const fallbackSignature = useMemo(() => {
        const name = authUser?.name?.trim() ?? ""
        const email = authUser?.email?.trim() ?? ""
        if (name && email) return `Best regards,\n${name}\n${email}`
        if (name) return `Best regards,\n${name}`
        if (email) return `Best regards,\n${email}`
        return "Best regards,\nRecruiting Team"
    }, [authUser?.name, authUser?.email])

    const effectiveSignature = defaultSignature || fallbackSignature

    const appendDefaultSignature = (content: string) => {
        const base = (content ?? "").replace(/\r\n/g, "\n").trimEnd()
        const signature = effectiveSignature.replace(/\r\n/g, "\n").trim()
        if (!signature) return base

        const normalizedBase = base.trimEnd()
        if (normalizedBase.endsWith(signature)) return base

        return normalizedBase ? `${normalizedBase}\n\n${signature}` : signature
    }

    const contentHasVisibleSignature = (content: string) => {
        const signature = effectiveSignature.replace(/\r\n/g, "\n").trim()
        const body = (content ?? "").replace(/\r\n/g, "\n").trimEnd()
        if (!signature) return false
        return body.endsWith(signature)
    }




    const handleOutreach = async () => {
        try {
            const formValues = outreachForm.getValues()

            if (!contentHasVisibleSignature(formValues.content)) {
                const contentWithSignature = appendDefaultSignature(formValues.content)
                outreachForm.setValue("content", contentWithSignature, { shouldDirty: true })
                toast.info("Signature added to draft", {
                    description: "Review the message with signature, then click Approve again.",
                })
                return
            }

            await sendEmail({
                data: {
                    recipientEmail: formValues.email,
                    subject: formValues.subject,
                    message: formValues.content,
                    candidate_id: candidateId,
                    contact_id: contactInfo?.[0].id ?? null,
                    job_id: jobId ?? null,
                    first_name: candidate.raw_json?.first_name ?? '',
                    last_name: candidate.raw_json?.last_name,
                }
            })
            console.log("[v0] Sending email:", formValues)
            toast.success("Sent!", {
                description: 'Email sent successfully',
                action: {
                    label: "Dismiss",
                    onClick: () => { },
                },
            })
            setEmailSent(true)
        } catch (err) {
            console.log('Error: ', err);
            toast.error("An error occured!", {
                description: `${err}`,
                action: {
                    label: "Dismiss",
                    onClick: () => { },
                },
            });
        }
    }

    const outreachForm = useForm<OutreachFormValues>({
        defaultValues: {
            email: contactInfo?.[0]?.email || "",
            subject: emailSubject,
            content: emailBody,
        },
    })

    useEffect(() => {
        if (data) {
            outreachForm.reset({
                email: contactInfo?.[0]?.email || "",
                subject: emailSubject,
                content: appendDefaultSignature(data.body ?? ""),
            })
        }
    }, [data, contactInfo, effectiveSignature, emailSubject])


    const handleFindEmail = () => {
        // Simulate email finding - in real app this would call an API
        const name = (candidate.name || candidate.first_name || "user").toLowerCase().replace(/\s+/g, ".")
        const company = (candidate.company || candidate.current_company || "company").toLowerCase().replace(/\s+/g, "")
        const foundEmail = `${name}@${company}.com`
        outreachForm.setValue("email", foundEmail)
    }



    const handleSendAnother = () => {
        setEmailSent(false)
        outreachForm.reset()
    }


  return (
    /* Same frame as the other record tabs: one surface, hairlines inside it.
       The blue left border was a leftover from the pre-Graphite panel. */
    <div className="max-w-full rounded-[10px] border bg-card px-4 py-4 @2xl:px-6 @2xl:py-5">
      <div className="flex items-center gap-2 border-b pb-3.5">
        <span className="text-[11.5px] font-medium text-muted-foreground">
          Outreach
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 text-xs text-muted-foreground"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
          <Button
            size="sm"
            variant={outreachType === "ai-email" ? "default" : "ghost"}
            onClick={() => setOutreachType("ai-email")}
            className="flex-1"
          >
            AI Email
          </Button>
          <Button
            size="sm"
            variant={outreachType === "linkedin" ? "default" : "ghost"}
            onClick={() => setOutreachType("linkedin")}
            className="flex-1"
          >
            LinkedIn
          </Button>
        </div>

        {/* Render LinkedIn component when linkedin is selected */}
        {outreachType === "linkedin" ? (
          <CandidateLinkedInOutreach candidate={candidate} />
        ) : (
          <>
            {emailSent ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">Email Sent Successfully!</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your outreach email has been sent to {outreachForm.getValues("email")}
                  </p>
                </div>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={handleSendAnother}>
                    Send Another Email
                  </Button>
                  <Button onClick={onClose}>Close</Button>
                </div>
              </div>
            ) : (
              <>
                {/* Email Form */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="outreach-email" className="flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground">
                      Email
                      {!outreachForm.watch("email") && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs bg-transparent"
                          onClick={handleFindEmail}
                        >
                          <Search className="h-3 w-3 mr-1" />
                          Find Email
                        </Button>
                      )}
                    </Label>
                    <Input
                      id="outreach-email"
                      {...outreachForm.register("email")}
                      placeholder="candidate@email.com"
                      className="bg-background"
                    />
                  </div>

                  <div>
                    <Label htmlFor="outreach-subject" className="flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground">
                      Subject:
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Edit className="h-3 w-3" />
                      </Button>
                    </Label>
                    <Input
                      id="outreach-subject"
                      {...outreachForm.register("subject")}
                      placeholder="Email subject"
                      className="bg-background"
                    />
                  </div>

                  <div>
                    <Label htmlFor="outreach-content" className="text-[11.5px] font-medium text-muted-foreground">Message</Label>
                    <textarea
                      id="outreach-content"
                      className="min-h-[300px] w-full rounded-md border bg-background p-3 font-mono text-[12.5px] outline-none transition-colors focus-visible:border-primary"
                      {...outreachForm.register("content")}
                      placeholder="Email content"
                    />
                  </div>

                  {/*
                    Generation is server side and can fail — most often with a
                    500 when the candidate has no `raw_json`, which the endpoint
                    reads without a null check. Saying so beats leaving the
                    subject and body silently blank, which reads as "still
                    loading" forever.
                  */}
                  {outreachError ? (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      <span className="flex-1">
                        Could not generate this message.{" "}
                        {!jobId
                          ? "This candidate is not on any job's pipeline, and outreach is written against a job."
                          : "The server rejected the request — the draft below is empty, not saved."}
                      </span>
                      <button
                        type="button"
                        onClick={() => refetch()}
                        className="font-medium underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>
                        {isFetching
                          ? "Generating…"
                          : data
                            ? "Generated just now"
                            : "Nothing generated yet"}
                      </span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                    onClick={handleOutreach}
                    disabled={isFetching || isSignatureLoading || isSendingEmail}
                    >
                    {isFetching || isSignatureLoading || isSendingEmail ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                    {isSendingEmail ? "Sending Email" : isFetching || isSignatureLoading ? "Content Loading..." : "Approve"}
                    </Button>

                    <Button
                        variant="secondary"
                        disabled={isRefetching}
                        onClick={() => refetch()}
                        className="flex items-center gap-2"
                    >
                        {isRefetching ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            "Regenerate"
                        )}
                    </Button>

                    <Button variant="outline">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    
                  </div>
                </div>

                <div className="rounded-md border bg-background px-4 py-3.5">
                  <div className="flex items-center gap-2 border-b pb-3">
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-[11.5px] font-medium text-muted-foreground">
                      AI generation context
                    </span>
                  </div>
                  <div className="space-y-4 pt-3.5 text-[12.5px]">
                    <div>
                      <Label className="text-[11.5px] font-medium text-muted-foreground">Bucket Context</Label>
                      <p className="text-muted-foreground">Bucket: Senior Engineers</p>
                      <p className="text-muted-foreground">
                        Description: Experienced software engineers for senior positions
                      </p>
                      <p className="text-muted-foreground">Touch Point Interval: Every 7 days</p>
                    </div>

                    <div>
                      <Label className="text-[11.5px] font-medium text-muted-foreground">Candidate Profile</Label>
                      <p className="text-muted-foreground">Role: {candidate.headline}</p>
                      <p className="text-muted-foreground">
                        Experience:{" "}
                        {candidate.total_experience_duration_months
                          ? Math.floor(candidate.total_experience_duration_months / 12)
                          : 0}{" "}
                        years in {candidate.experience?.[0]?.position_title?.toLowerCase()}
                      </p>
                      <p className="text-muted-foreground">
                        Skills: {candidate.inferred_skills?.slice(0, 3).join(", ")}
                      </p>
                      <p className="text-muted-foreground">Interests: distributed systems, high-performance computing</p>
                    </div>

                    <div>
                      <Label className="text-[11.5px] font-medium text-muted-foreground">Keywords Used</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {["check-in", "backend role", "distributed systems", "search"].map((keyword) => (
                          <Badge key={keyword} variant="secondary" className="text-xs">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-[11.5px] font-medium text-muted-foreground">Personalization Factors</Label>
                      <ul className="text-muted-foreground text-xs space-y-1 mt-1">
                        <li>• Previous interview at InnovateAI mentioned</li>
                        <li>• Interest in distributed systems referenced</li>
                        <li>• Natural, non-pushy tone for warm outreach</li>
                        <li>• Maintains relationship without being salesy</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
